import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Activity as ActivityIcon, User as UserIcon } from "lucide-react";
import api from "@/lib/api";

export default function CollaboratorActivity() {
  const { t } = useTranslation();
  const [collabs, setCollabs] = useState([]);
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/collaborators").then(({ data }) => {
      setCollabs(data.items || []);
      if ((data.items || []).length > 0 && !selected) setSelected(data.items[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/collaborators/${selected}/activity`);
        setRows(data.items || []);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
    })();
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="collab-activity-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Collaborators</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("collab.activity_title", "Collaborator activity")}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{t("collab.activity_hint", "Every action performed by a collaborator — read-only audit trail.")}</p>
      </div>

      <div className="flex items-center gap-2">
        <UserIcon className="w-4 h-4 text-slate-400" />
        <select data-testid="collab-activity-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm">
          {collabs.map((c) => (<option key={c.id} value={c.id}>{`${c.name} (${c.username})`}</option>))}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50">
                <th className="px-4 py-2">Date/time</th>
                <th className="px-4 py-2">Action</th>
                <th className="px-4 py-2">Target</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">IP</th>
                <th className="px-4 py-2">Payload</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">{t("collab.activity_empty", "No activity yet.")}</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 font-semibold text-slate-900">{r.action}</td>
                  <td className="px-4 py-2 text-slate-700">{r.target_type || "—"} · {(r.payload && (r.payload.target_username || r.payload.username)) || r.target_id || "—"}</td>
                  <td className="px-4 py-2"><span className={"text-xs font-semibold px-2 py-0.5 rounded-full border " + (r.status === "SUCCESS" ? "bg-emerald-50 text-emerald-700 border-emerald-500" : "bg-rose-50 text-rose-700 border-rose-500")}>{r.status || "—"}</span></td>
                  <td className="px-4 py-2 text-slate-700 font-mono text-xs">{(r.payload && r.payload.ip) || "—"}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-md truncate font-mono text-[11px]" title={JSON.stringify(r.payload || {})}>{JSON.stringify(r.payload || {}).slice(0, 90)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
